import type { HwcgGroupId } from './hwcgGroupActions';
import { hwcgEocCompanyMatches, type HwcgEocChannel } from './hwcgEocNotice';
import { NotificationType } from 'hooks/useNotification';

export type HwcgNotifyListContact = {
  id: string;
  constituency: string;
  memberCompany?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email?: string | null;
};

/**
 * Live Group Actions Notify uses the create-notification modal instead of the
 * checkbox list.
 */
export function hwcgLiveNotifyUsesComposer(
  sendMode: boolean,
  action: 'activate' | 'notify'
): boolean {
  return sendMode && action === 'notify';
}

/**
 * Contacts the Notify composer may offer. Each group uses its contact-list
 * section.
 */
export function hwcgNotifyComposerContacts<T extends HwcgNotifyListContact>(
  groupId: HwcgGroupId,
  contacts: T[],
  companyName: string
): T[] {
  if (groupId === 'eoc') {
    return contacts.filter(
      (contact) =>
        contact.constituency === 'eoc' &&
        hwcgEocCompanyMatches(contact.memberCompany, companyName)
    );
  }
  if (groupId === 'responsible_party') {
    return contacts.filter(
      (contact) =>
        (contact.constituency === 'mutual_aid' ||
          contact.constituency === 'responsible_party') &&
        hwcgEocCompanyMatches(contact.memberCompany, companyName)
    );
  }
  if (groupId === 'mutual_aid') {
    return contacts.filter(
      (contact) =>
        contact.constituency === 'mutual_aid' &&
        !hwcgEocCompanyMatches(contact.memberCompany, companyName)
    );
  }
  return contacts.filter(
    (contact) => contact.constituency === 'service_provider'
  );
}

export function hwcgNotifyContactLabel(contact: HwcgNotifyListContact): string {
  const name =
    [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
    contact.name?.trim() ||
    'Recipient';
  const email = contact.email?.trim();
  return email ? `${name} (${email})` : name;
}

/** One SMS segment. The generic composer stays here. */
export const SMS_SINGLE_SEGMENT_MAXIMUM_LENGTH = 160;

/** Concatenated SMS, the cap the send path still enforces. */
export const HWCG_SMS_MAXIMUM_LENGTH = 1600;

/**
 * Group Actions notify needs the longer cap so the template fits. The generic
 * Create Notification modal stays at one segment.
 */
export function smsComposerMaximumLength(
  hwcg: boolean,
  contactList: boolean
): number {
  return hwcg && contactList
    ? HWCG_SMS_MAXIMUM_LENGTH
    : SMS_SINGLE_SEGMENT_MAXIMUM_LENGTH;
}

/**
 * Which audience controls the composer shows. Contact-list mode replaces the
 * roster picker and hides the reply menu and the escalation control. The group
 * send keeps in-app Confirm Receipt on its own.
 */
export function notificationComposeAudience(contactList: boolean): {
  roster: boolean;
  roles: boolean;
  teams: boolean;
  map: boolean;
  schedule: boolean;
  contactList: boolean;
  replyMenu: boolean;
  escalation: boolean;
} {
  return {
    roster: !contactList,
    roles: !contactList,
    teams: !contactList,
    map: !contactList,
    schedule: !contactList,
    contactList,
    replyMenu: !contactList,
    escalation: !contactList,
  };
}

export function hwcgChannelsToNotificationTypes(
  channels: HwcgEocChannel[]
): NotificationType[] {
  const types: NotificationType[] = [];
  if (channels.includes('inApp')) types.push(NotificationType.IN_APP);
  if (channels.includes('email')) types.push(NotificationType.EMAIL);
  if (channels.includes('sms')) types.push(NotificationType.SMS);
  if (channels.includes('voice')) types.push(NotificationType.TTS_CELL);
  return types.length > 0 ? types : [NotificationType.IN_APP];
}

export function hwcgChannelsFromNotificationTypes(
  types: NotificationType[],
  previous: HwcgEocChannel[]
): HwcgEocChannel[] {
  const next: HwcgEocChannel[] = [];
  if (types.includes(NotificationType.IN_APP)) next.push('inApp');
  if (types.includes(NotificationType.EMAIL)) next.push('email');
  if (types.includes(NotificationType.SMS)) next.push('sms');
  if (
    types.includes(NotificationType.TTS_CELL) ||
    types.includes(NotificationType.TTS_HOME) ||
    previous.includes('voice')
  ) {
    next.push('voice');
  }
  return next.length > 0 ? next : previous;
}

/**
 * The group send has one script. Prefer a body the user changed from the
 * template.
 */
export function hwcgEditedNoticeScript(
  seed: string,
  fields: {
    appMessage?: string | null;
    emailMessage?: string | null;
    smsMessage?: string | null;
    phoneMessage?: string | null;
    teamsMessage?: string | null;
  }
): string {
  const template = seed.trim();
  const bodies = [
    fields.appMessage,
    fields.emailMessage,
    fields.smsMessage,
    fields.phoneMessage,
    fields.teamsMessage,
  ]
    .map((value) => value?.trim() ?? '')
    .filter(Boolean);
  return bodies.find((body) => body !== template) || bodies[0] || template;
}
